import React, { FC, ReactNode } from 'react';
import Select, { CSSObjectWithLabel, StylesConfig } from 'react-select';
import { AppEnum, REACT_SELECT_SEARCHABLE_THRESHOLD, SelectOptionType, useSelectMenuStatus } from '@/Shared';
import { MultiValue, SingleValue } from 'react-select';
import './ReactSelectField.css';


interface ReactSelectFieldProps {
	label: string;
	icon?: ReactNode;
	name: string;
	options: AppEnum[];
	required?: boolean;
	readOnly?: boolean;
	placeholder?: string;
	isMulti?: boolean;
	field: {
		name: string;
		value: any;
	}
	form: {
		setFieldValue: (name: string, value: any) => void;
		setFieldTouched?: (name: string, touched?: boolean) => void;
	}
	errorMessage?: (fieldNale: string) => ReactNode;
	scrollParent?: HTMLElement,
	onChange?: (selectedOptions: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>) => void,
}

const ReactSelectField: FC<ReactSelectFieldProps> = ({
	field,
	form,
	label,
	icon,
	options,
	required = false,
	readOnly = false,
	placeholder,
	isMulti = false,
	scrollParent,
	onChange,
	errorMessage
}) => {

	const { scrollParent: parent, menuIsOpen, openMenu, closeMenu } = useSelectMenuStatus(scrollParent)
	const isSearchable = (options?.length ?? 0) > REACT_SELECT_SEARCHABLE_THRESHOLD;


	// Map Formik IDs -> react-select values
	const value =
		options
			.filter(opt => {
				return isMulti
					? field.value?.includes(String(opt.value)) :
					String(field.value) === String(opt.value);
			})
			.map(opt => {
				return {
					value: opt.value,
					label: opt.label
				}
			});

	const styles: StylesConfig = {
			menuPortal: (base: CSSObjectWithLabel) => ({
				...base,
				zIndex: 9999,
			}),
		};

	const title = options.find(opt => opt.value === field?.value)?.label ?? "Aucun élément sélectionné";

	return (
		<label className={`SelectField form-field-label ${readOnly ? 'readonly' : ''}`}>
			<div>
				{label}
				{required && <span className="asterisk"> *</span>}
			</div>

			<div className="form-group">
				<div className="Field-wrapper" title={title}>
					{icon && (
						<div className="field-icon">{icon}</div>)
					}

					<Select
						isSearchable={isSearchable}
						menuIsOpen={menuIsOpen}
						onMenuOpen={openMenu}
						onMenuClose={closeMenu}
						closeMenuOnScroll={true}
						openMenuOnClick={true}
						isClearable={!required}
						required={required}
						isMulti={isMulti}
						isDisabled={readOnly}
						options={options}
						value={value}
						placeholder={placeholder ?? "Choisir un item de la liste"}
						classNamePrefix={`react-select ${icon ? 'select-with-icon' : ''}`}
						onChange={
							(selected) => {
								form.setFieldValue(
									field.name,
									isMulti
										? (selected ? (selected as MultiValue<SelectOptionType>).map(o => o.value) : [])
										: (selected ? (selected as (SingleValue<SelectOptionType> | null))?.value : null)
								);
								onChange?.(selected);
							}
						}
						onBlur={() => form.setFieldTouched?.(field.name, true)}

						menuPosition="fixed"
						menuPlacement="auto"
						menuPortalTarget={parent}
						styles={{
							menuPortal: base => ({ ...base, zIndex: 9999 }),
						}}

					/>
				</div>
				{errorMessage?.(field.name)}
			</div>
		</label>
	);
};

export default ReactSelectField;
